import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, Fragment, useState, useCallback } from "react";

import { Box, Stack } from "@mui/system";
import { Card, List, Button, Divider, ListItem, TextField, CardHeader, Pagination, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";
import { useOrgTenant } from "src/hooks/auth/use-org-tenant";

import { useTranslate } from "src/locales";
import { ICompanyListItem } from "src/services/connections/connections.types";
import { PICKER_PAGE, pickerSortConfig, pickerHelperText, pickerNoOptionsText, buildPickerColumnFilters } from "src/utils/picker-search";
import { useGetCompanyLinksQuery, useGetMyCompaniesLeanQuery, useAddCompanyLinksMutation, useRemoveCompanyLinkMutation } from "src/services/connections/connections.service";

import Iconify from "src/components/iconify";
import EmptyContent from "src/components/empty-content";
import { ConfirmDialog } from "src/components/custom-dialog";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";

import { LoadingCardItem } from "src/sections/admin/billing/payment/payment-card-item";

type Props = {
  profileId: string;
}

type PickableCompany = Pick<ICompanyListItem, 'id' | 'company_name'>;

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

// The card lists links 5 at a time, so the picker loads them all to know which are already linked.
const ALL_LINKS_PAGE_OPTIONS = {
  page: 1,
  per_page: 100
};

const CompanyLinksCard = ({ profileId }: Props) => {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const addLinkControl = useBoolean(false);

  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE_OPTIONS);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState<PickableCompany[]>([]);

  const router = useRouter();

  const [addCompanyLinks, { isLoading: isAddingCompanyLinks }] = useAddCompanyLinksMutation()
  const [removeCompanyLink, { isLoading: isRemovingCompanyLink }] = useRemoveCompanyLinkMutation()

  const { data: companyLinks, isLoading: isLoadingLinks } = useGetCompanyLinksQuery({
    profile_id: profileId,
    params: pageOptions
  }, {
    skip: !profileId,
  });

  const { data: allCompanyLinks } = useGetCompanyLinksQuery({
    profile_id: profileId,
    params: ALL_LINKS_PAGE_OPTIONS
  }, {
    skip: !profileId || !addLinkControl.value,
  });

  // Name search on the lean list, always from the first page and only while the dialog is open
  // (JT1-1586). It used to reuse the linked list's pageOptions, so page 2 of links searched page 2.
  const { currentData: companies, isFetching: isSearchingCompanies, isError: isCompanySearchError } = useGetMyCompaniesLeanQuery(
    {
      params: {
        ...PICKER_PAGE,
        ...pickerSortConfig(companySearchQuery),
        column_filters: buildPickerColumnFilters(companySearchQuery),
      },
    },
    {
      skip: !addLinkControl.value,
    }
  );

  const linkedCompanyIds = useMemo(
    () => new Set(allCompanyLinks?.results?.map((link) => link.company_profile.id) ?? []),
    [allCompanyLinks]
  );

  const onChangePage = useCallback((event: React.ChangeEvent<unknown>, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber
    }));
  }, []);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const onAddLink = () => {
    addLinkControl.onTrue();
  }

  const onLinkModalClose = useCallback(() => {
    addLinkControl.onFalse();
    setSelectedCompanies([]);
    setCompanySearchQuery("");
  }, [addLinkControl, setSelectedCompanies, setCompanySearchQuery]);

  const handleAddLinks = useCallback(async () => {

    if (!selectedCompanies.length) return;

    try {
      await addCompanyLinks({
        profile_id: profileId,
        data: {
          company_profiles: selectedCompanies.map((company) => company.id)
        }
      }).unwrap();

      onLinkModalClose();

      enqueueSnackbar(t('companies.profile.api.add_company_link.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.add_company_link.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, addCompanyLinks, profileId, selectedCompanies, t]);

  const handleRemoveLink = useCallback(async (linkId: string) => {
    try {
      await removeCompanyLink({
        profile_id: profileId,
        link_id: linkId
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.remove_company_link.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.remove_company_link.default_error'), { variant: 'error' });
    }
  }, [profileId, removeCompanyLink, t]);

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
              <Iconify
                icon="material-symbols:dataset-linked-outline" />
              <Box>{t('companies.profile.linked_companies.title')}</Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={onAddLink}
              startIcon={<Iconify icon="eva:plus-fill" />}
              sx={{
                color: 'text.primary',
                textTransform: 'capitalize',
              }}>
              {t('companies.profile.linked_companies.add_link')}
            </Button>
          </Stack>
        }
      />
      <CardContent sx={{ mb: 2 }}>

        {
          isLoadingLinks &&
          <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }

        {
          (!isLoadingLinks && !companyLinks?.count) && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <EmptyContent
                title={t('companies.profile.linked_companies.empty')}
                description={t('companies.profile.linked_companies.empty_description')}
                sx={{ width: '100%' }}
              />
            </Stack>
          )
        }

        {
          (!isLoadingLinks && !!companyLinks?.count) && (
            <Stack direction="column" gap={2} justifyContent="start" alignItems="start" sx={{ width: '100%' }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {companyLinks?.results?.map((link) => (
                  <Fragment key={link.id}>
                    <ListItem
                      key={link.id}
                      sx={{ pt: 1 }}
                      secondaryAction={
                        <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              router.push(
                                paths.dashboard.companies.detail(link.company_profile.id, tenantType, link.company_profile.company_type)
                              )
                            }}
                          >
                            {t('common.view')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            disabled={isRemovingCompanyLink}
                            onClick={() => handleRemoveLink(link.id)}
                          >
                            {t('companies.profile.linked_companies.remove_link')}
                          </Button>
                        </Stack>
                      }>
                      <ListItemText
                        sx={{ pr: 4 }}
                        primary={
                          <TruncatedTextField
                            text={link.company_profile.company_name}
                            mode="characters"
                            limit={30}
                            showMoreEnabled={false}
                            variant="subtitle2"
                            style={{ textTransform: 'capitalize' }}
                          />
                        }
                        secondary={t(`connections.table.company_type.${link.company_profile.company_type}`)}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                  </Fragment>
                ))}
              </List>

              <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%', mt: 2 }}>
                <Pagination
                  count={companyLinks?.count ? Math.ceil(companyLinks.count / pageOptions.per_page) : 1}
                  page={pageOptions.page}
                  onChange={onChangePage}
                  size="small"
                />
              </Stack>
            </Stack>

          )
        }

      </CardContent>

      <ConfirmDialog
        open={addLinkControl.value}
        title={t('companies.profile.linked_companies.dialog_title')}
        onClose={onLinkModalClose}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            multiple
            options={companies?.results ?? []}
            value={selectedCompanies}
            // The server already matched the text; MUI's default label filter would drop its results.
            filterOptions={(options) => options}
            loading={isSearchingCompanies}
            noOptionsText={pickerNoOptionsText(t, isCompanySearchError)}
            // Shown disabled rather than hidden, so a search never silently shrinks to nothing.
            getOptionDisabled={(option) => option.id === profileId || linkedCompanyIds.has(option.id)}
            // 'reset' is MUI re-syncing the input after a selection or re-render, not the user typing.
            onInputChange={(_e, value, reason) => reason !== 'reset' && onSearchQueryChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'companies.profile.linked_companies.search'
                )}
                placeholder={t(
                  'companies.profile.linked_companies.search_placeholder'
                )}
                helperText={pickerHelperText(t, companies)}
              />
            )}
            onChange={(_e, value: PickableCompany[]) => setSelectedCompanies(value)}
            renderOption={({ key, ...rest }, option: PickableCompany) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.company_name}`}
                  </Typography>
                  {linkedCompanyIds.has(option.id) && (
                    <Typography variant="caption" color="text.secondary">
                      {t('common.picker.already_linked')}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => option.company_name as string}
            isOptionEqualToValue={(option, value) =>
              option && option.id === value.id
            }
            getOptionKey={(option) => option.id}
          />
        }
        action={
          <Button variant="contained" disabled={!selectedCompanies.length || isAddingCompanyLinks} onClick={handleAddLinks}>
            {t('companies.profile.linked_companies.associate')}
          </Button>
        }
      />
    </Card >
  )

}

export default CompanyLinksCard;
